import { z } from 'zod'

// Zod 4 compila los objetos con `new Function`, que la CSP del portal bloquea
// (Firefox lo reporta como error de consola). Sin JIT valida igual.
z.config({ jitless: true })

/**
 * Contratos de Administración (P03 · C8), compartidos entre el frontend y
 * las funciones. Lo que valida el formulario es exactamente lo que valida el
 * servidor; los mensajes son los que ve la persona.
 */

/* ── Helpers ────────────────────────────────────────────── */

const texto = (max: number, nombre: string) =>
  z
    .string({ error: `${nombre} es obligatorio.` })
    .trim()
    .min(1, `${nombre} es obligatorio.`)
    .max(max, `${nombre}: hasta ${String(max)} caracteres.`)

/** Fecha de calendario "AAAA-MM-DD" o vacío. */
const fechaOpcional = z
  .string()
  .nullable()
  .transform((v) => (v?.trim() ? v.trim() : null))
  .pipe(
    z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Fecha inválida.')
      .nullable(),
  )

export const COLORES_PILAR = [1, 2, 3, 4, 5, 6, 7, 8] as const
export type ColorPilar = (typeof COLORES_PILAR)[number]

/** Acepta un ID de carpeta o un enlace de Drive pegado; se guarda el ID. */
export function idDeCarpetaDrive(valor: string): string | null {
  const v = valor.trim()
  if (!v) return null
  const enlace = /folders\/([A-Za-z0-9_-]{10,})/.exec(v)
  if (enlace?.[1]) return enlace[1]
  if (/^[A-Za-z0-9_-]{10,}$/.test(v)) return v
  return null
}

export const esMailValido = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())

/* ── Cliente ────────────────────────────────────────────── */

export const clienteEntrada = z.object({
  nombre: texto(120, 'El nombre'),
  usuarioInstagram: z
    .string({ error: 'El usuario de Instagram es obligatorio.' })
    .trim()
    .transform((v) => v.replace(/^@/, ''))
    .pipe(
      z
        .string()
        .min(1, 'El usuario de Instagram es obligatorio.')
        .max(40, 'El usuario de Instagram: hasta 40 caracteres.')
        .regex(/^[A-Za-z0-9._]+$/, 'Sólo letras, números, puntos y guiones bajos.'),
    ),
  driveFolder: z
    .string({ error: 'La carpeta de Drive es obligatoria.' })
    .trim()
    .min(1, 'La carpeta de Drive es obligatoria.')
    .refine((v) => idDeCarpetaDrive(v) !== null, 'Pegá el enlace o el ID de la carpeta.'),
  activo: z.boolean().default(true),
  /**
   * DEC-10: la profesional que atiende al cliente; los mails salen desde su
   * casilla. null = sin asignar. Si no viene, no se cambia.
   */
  profesionalAsignadoId: z.uuid().nullable().optional(),
})
export type ClienteEntrada = z.input<typeof clienteEntrada>

/* ── Proyecto ───────────────────────────────────────────── */

export const proyectoEntrada = z
  .object({
    clienteId: z.uuid(),
    nombre: texto(120, 'El nombre'),
    periodoDesde: fechaOpcional,
    periodoHasta: fechaOpcional,
    /** Sólo al crear: proyecto del mismo cliente del que se copian los pilares. */
    copiarPilaresDe: z.uuid().nullable().default(null),
  })
  .refine((p) => !p.periodoDesde || !p.periodoHasta || p.periodoDesde <= p.periodoHasta, {
    message: 'El período termina antes de empezar.',
    path: ['periodoHasta'],
  })
export type ProyectoEntrada = z.input<typeof proyectoEntrada>

/* ── Pilar ──────────────────────────────────────────────── */

export const pilarEntrada = z.object({
  proyectoId: z.uuid(),
  nombre: texto(60, 'El nombre'),
  color: z.number().int().min(1).max(8),
  descripcion: z
    .string()
    .trim()
    .max(140, 'La descripción: hasta 140 caracteres.')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .default(null),
})
export type PilarEntrada = z.input<typeof pilarEntrada>

/* ── Invitar persona ────────────────────────────────────── */

export const invitacionEntrada = z.object({
  clienteId: z.uuid(),
  nombre: texto(120, 'El nombre'),
  email: z
    .string({ error: 'El mail es obligatorio.' })
    .trim()
    .toLowerCase()
    .refine(esMailValido, 'Escribí un mail válido.'),
  proyectoIds: z.array(z.uuid()).min(1, 'Elegí al menos un proyecto.'),
})
export type InvitacionEntrada = z.input<typeof invitacionEntrada>

/* ── Equipo RASTRO ──────────────────────────────────────── */

export const integranteEntrada = z.object({
  nombre: texto(120, 'El nombre'),
  email: z
    .string({ error: 'El mail es obligatorio.' })
    .trim()
    .toLowerCase()
    .refine(esMailValido, 'Escribí un mail válido.'),
})
export type IntegranteEntrada = z.input<typeof integranteEntrada>

/* ── Respuestas ─────────────────────────────────────────── */

export interface ClienteAdmin {
  id: string
  nombre: string
  usuarioInstagram: string
  driveFolderId: string
  activo: boolean
  proyectosActivos: number
  profesionalAsignadoId: string | null
  /** Nombre de la profesional asignada, para mostrarlo. */
  profesionalAsignado: string | null
}

export interface ProyectoAdmin {
  id: string
  clienteId: string
  nombre: string
  /** "AAAA-MM-DD" en Buenos Aires, o null. */
  periodoDesde: string | null
  periodoHasta: string | null
  activo: boolean
}

export interface PilarAdmin {
  id: string
  proyectoId: string
  nombre: string
  color: number
  descripcion: string | null
  activo: boolean
  /** Publicaciones vigentes que lo usan: si hay, no se da de baja. */
  publicaciones: number
}

export interface AccesoAdmin {
  usuarioId: string
  nombre: string
  email: string
  estado: 'activa' | 'invitada'
  /** ISO. Fecha de aceptación si está activa; de invitación si no. */
  fecha: string
  /**
   * Otros proyectos del mismo cliente que la persona sigue viendo. Quitar el
   * acceso a uno no toca los demás, y hay que decirlo: si le queda uno solo,
   * el portal la lleva directo a él y parece que el quite no funcionó.
   */
  otrosProyectos: string[]
}

export interface DetalleProyectoAdmin {
  proyecto: ProyectoAdmin
  /** `remitente`: quien firma los mails del cliente (DEC-10). */
  cliente: { id: string; nombre: string; remitente: string }
  pilares: PilarAdmin[]
  personas: AccesoAdmin[]
}

export interface IntegranteEquipo {
  id: string
  nombre: string
  email: string
}

/** Quien puede quedar asignada a un cliente: equipo con casilla del dominio. */
export interface Profesional {
  id: string
  nombre: string
  email: string
}

export interface ResultadoInvitacion {
  usuarioId: string
  /** Ya tenía acceso a otros proyectos: "Le vamos a sumar este." */
  yaEraUsuario: boolean
}
